import { Plus, Search } from "lucide-react";

import { Accent, SplitFeature, panel } from "@/features/platform/components/landing-ui";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";

/** The sample library and day. Exercise names only; the load is a sample, not advice. */
const LIBRARY = [
  { id: "squat", name: () => m.platform_plan_squat(), added: true },
  { id: "bench", name: () => m.platform_plan_bench(), added: false },
  { id: "rdl", name: () => m.platform_plan_rdl(), added: true },
  { id: "pullup", name: () => m.platform_plan_pullup(), added: false },
  { id: "row", name: () => m.platform_plan_row(), added: true },
];
const DAY = [
  { id: "squat", name: () => m.platform_plan_squat(), sets: "4", reps: "6", load: "80" },
  { id: "rdl", name: () => m.platform_plan_rdl(), sets: "3", reps: "8", load: "70" },
  { id: "row", name: () => m.platform_plan_row(), sets: "3", reps: "10", load: "24", fresh: true },
];

/**
 * Writing a training plan: the library on one side, the day being built on the other. Told
 * beside its picture, because the editor is the feature.
 */
export const PlanBuilder = () => (
  <SplitFeature
    id="plans"
    eyebrow={m.platform_feature_plans_title()}
    title={
      <>
        {m.platform_plan_title_lead()}
        <br />
        <Accent>{m.platform_plan_title_tail()}</Accent>
      </>
    }
    body={m.platform_plan_body()}
    className="grid gap-3.5 sm:grid-cols-[0.85fr_1.15fr]"
  >
    <div aria-hidden className={cn(panel, "p-4 text-[13px]")}>
      <span className="flex items-center gap-2 rounded-xl bg-[var(--site-well)] px-3 py-2.5 text-muted-foreground">
        <Search className="size-4" />
        {m.platform_plan_search()}
      </span>
      <ul className="mt-3 grid gap-1.5">
        {LIBRARY.map((exercise) => (
          <li key={exercise.id} className="flex items-center justify-between gap-2 rounded-xl px-2 py-1.5">
            <span className="truncate">{exercise.name()}</span>
            <span
              className={cn(
                "grid size-6 shrink-0 place-items-center rounded-full",
                exercise.added ? "bg-primary text-primary-foreground" : "bg-[var(--site-well)] text-muted-foreground",
              )}
            >
              <Plus className="size-3.5" />
            </span>
          </li>
        ))}
      </ul>
    </div>

    <div aria-hidden className={cn(panel, "p-4 text-[13px]")}>
      <div className="flex items-baseline justify-between gap-3">
        <b className="text-base font-semibold tracking-[-0.02em]">{m.platform_plan_block()}</b>
        <span className="text-xs text-muted-foreground">{m.platform_plan_week_of({ week: 2, total: 12 })}</span>
      </div>
      <div className="mt-3 flex gap-1">
        {[m.platform_plan_day_a(), m.platform_plan_day_b(), m.platform_plan_day_c()].map((day, index) => (
          <span
            key={day}
            className={cn(
              "rounded-full px-3 py-1 text-xs font-semibold",
              index === 0 ? "bg-foreground text-background" : "bg-[var(--site-well)] text-muted-foreground",
            )}
          >
            {day}
          </span>
        ))}
      </div>
      <div className="mt-3 grid grid-cols-[1fr_repeat(3,44px)] gap-x-2 px-2 pb-1.5 text-[11px] text-muted-foreground">
        <span />
        <span className="text-center">{m.platform_plan_sets()}</span>
        <span className="text-center">{m.platform_plan_reps()}</span>
        <span className="text-center">{m.platform_plan_load()}</span>
      </div>
      <div className="grid gap-1.5">
        {DAY.map((row) => (
          <div
            key={row.id}
            className={cn(
              "grid grid-cols-[1fr_repeat(3,44px)] items-center gap-x-2 rounded-xl px-2 py-2",
              row.fresh ? "bg-primary/15 outline outline-1 outline-primary/50" : "bg-[var(--site-well)]",
            )}
          >
            <span className="truncate font-medium">{row.name()}</span>
            {[row.sets, row.reps, row.load].map((value, index) => (
              <b key={`${row.id}-${index}`} className="text-center font-semibold tabular-nums">
                {value}
              </b>
            ))}
          </div>
        ))}
      </div>
    </div>
  </SplitFeature>
);
